import { ChangeEvent, ComponentProps, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { useFormikContext } from 'formik';

import { AutoExplodeZip } from '../AutoExplodeZip';
import MaskedSsnInput from '../MaskedSsn/MaskedSsnInput';
import { GridFormColumn, GridFormRow } from '../shared/GridForm';
import Input from '../shared/Input';
import Select from '../shared/Select';

import { CreditAppOnDeal } from './useCreditAppInitialValues';

import { phoneNumberMask } from '../../constants/masks';
import SELECT_OPTIONS from '../../constants/selectOptions';

type OriginalLesseeProps = ComponentProps<typeof GridFormColumn> & {
  customHandleChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
};

const OriginalLesseeForm = ({ customHandleChange, ...props }: OriginalLesseeProps) => {
  const { handleBlur, values } = useFormikContext<CreditAppOnDeal>();
  const [isAddressLineFocused, setAddressLineFocused] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);

  return (
    <GridFormColumn {...props}>
      <GridFormRow minChildWidth={270}>
        <Input label="First Name" name="original_lessee.first_name" onChange={customHandleChange} />
        <Input label="Last Name" name="original_lessee.last_name" onChange={customHandleChange} />
      </GridFormRow>
      <GridFormRow minChildWidth={270}>
        <Input
          label="Phone Number"
          name="original_lessee.phone_number"
          onChange={customHandleChange}
          mask={phoneNumberMask}
          copyButton
        />
        <Input
          label="Email"
          name="original_lessee.email"
          onChange={customHandleChange}
          copyButton
        />
      </GridFormRow>
      <GridFormRow minChildWidth={270}>
        <Input
          label="Address"
          name="original_lessee.address_line"
          onChange={customHandleChange}
          onFocus={() => setAddressLineFocused(true)}
          onBlur={(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            setAddressLineFocused(false);
            handleBlur(e);
          }}
        />
        <Box />
      </GridFormRow>
      <GridFormRow minChildWidth={180}>
        <Input label="Zip" name="original_lessee.zip" onChange={customHandleChange} />
        <Input
          label="City"
          name="original_lessee.city"
          onChange={customHandleChange}
          isLoading={zipLoading}
        />
        <Select
          label="State"
          name="original_lessee.state"
          onChange={customHandleChange}
          options={SELECT_OPTIONS.US_STATES}
          isLoading={zipLoading}
        />
        <Input
          label="County"
          name="original_lessee.county"
          onChange={customHandleChange}
          isLoading={zipLoading}
        />
        <AutoExplodeZip
          name="original_lessee"
          isAddressLineFocused={isAddressLineFocused}
          setZipLoading={setZipLoading}
          isOriginalLessee
        />
      </GridFormRow>
      <GridFormRow minChildWidth={270}>
        <MaskedSsnInput
          ssnIdField="original_lessee.ssn_id"
          ssnValidField="original_lessee.has_full_ssn"
          ssnId={values.original_lessee?.ssn_id ?? null}
          replaceFixedCharacters
          optional
        />
        <Box />
      </GridFormRow>
    </GridFormColumn>
  );
};

export default OriginalLesseeForm;
